// Discord Activity handshake. Real path inside the iframe; DiscordSDKMock in a plain
// browser so the whole app runs offline. Either way the output is the same: an
// access_token (the Bearer for every backend call) and the authenticated uid.
//
// The client NEVER sends its uid to the backend — the backend derives it from the token.
// uid is kept here only for display and as the epoch-0 client_seed fallback.

import { DiscordSDK, DiscordSDKMock, patchUrlMappings } from '@discord/embedded-app-sdk';
import { paintBoot } from './boot';
import { API_BASE, API_PREFIX, ARCADE_TARGET, DISCORD_CLIENT_ID, IS_DISCORD, OAUTH_SCOPES } from './config';

export interface Session {
  uid: number;
  accessToken: string;
  username: string;
}

// The backend's guild gate said no: the user is not in an allowlisted server.
export class GuildNotAllowedError extends Error {
  constructor() {
    super('guild_not_allowed');
    this.name = 'GuildNotAllowedError';
  }
}

type AnySdk = DiscordSDK | DiscordSDKMock;

let sdk: AnySdk | null = null;

export function getSdk(): AnySdk {
  if (sdk) return sdk;
  sdk = IS_DISCORD
    ? new DiscordSDK(DISCORD_CLIENT_ID)
    : new DiscordSDKMock(DISCORD_CLIENT_ID, null, null, null);
  return sdk;
}

async function exchangeCode(code: string): Promise<string> {
  const res = await fetch(`${API_BASE}/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (res.status === 403) throw new GuildNotAllowedError();
  if (!res.ok) throw new Error(`token exchange failed (${res.status})`);
  const body = (await res.json()) as { access_token?: string };
  if (!body.access_token) throw new Error('token exchange returned no access_token');
  return body.access_token;
}

export async function startSession(): Promise<Session> {
  const client = getSdk();
  // Stage paints (never token values): the handshake can stall without throwing, and the
  // last painted stage tells us exactly where.
  paintBoot('[sdk] ready() — waiting for the Discord handshake…');
  await client.ready();
  paintBoot('[sdk] ready ✓');

  if (IS_DISCORD) {
    if (ARCADE_TARGET) {
      // Route iframe fetches to the backend through Discord's activity proxy (CSP).
      patchUrlMappings([{ prefix: API_PREFIX, target: ARCADE_TARGET }]);
      paintBoot(`[sdk] url mappings patched → ${ARCADE_TARGET}`);
    }
    paintBoot('[sdk] authorize() — requesting the OAuth code…');
    const { code } = await client.commands.authorize({
      client_id: DISCORD_CLIENT_ID,
      response_type: 'code',
      state: '',
      prompt: 'none',
      scope: OAUTH_SCOPES as never,
    });
    paintBoot('[sdk] authorize ✓ — exchanging the code at the backend…');
    const accessToken = await exchangeCode(code);
    paintBoot('[sdk] token ✓ — authenticate()…');
    const auth = await client.commands.authenticate({ access_token: accessToken });
    paintBoot('[sdk] authenticate ✓');
    return {
      uid: Number(auth.user.id),
      accessToken,
      username: auth.user.username ?? 'wanderer',
    };
  }

  // Mock mode: pick the dev uid from ?uid= (default 300), let the mock backend mint a
  // token that encodes it. No real Discord modal.
  const devUid = Number(new URLSearchParams(window.location.search).get('uid') ?? '300');
  const accessToken = await exchangeCode(`mock:${devUid}`);
  return { uid: devUid, accessToken, username: `dev-${devUid}` };
}
